import React, { useState } from "react";
import { Modal, Toggle, ToolTip, useSnackBar } from "@truevault/aquilo";
import clsx from "clsx";
import { useOnboardingProgress } from "../../../../common/hooks/useOnboardingProgress";
import { OrganizationPublicId } from "../../../../common/service/server/Types";
import type { VendorInventoryItem } from "./vendorInventoryUtils";

export interface VendorDataSourceRecipientSectionProps {
  orgId: OrganizationPublicId;
  vendor: VendorInventoryItem;
  setIsDisabledWhileLoading: (value: boolean) => void;
  addDataSource: (vendorId: string) => Promise<void>;
  removeDataSource: (vendorId: string) => Promise<void>;
  /** Drops the recipient role for this context only; the vendor keeps any source role. */
  removeRecipientOnly: (vendorId: string) => Promise<void>;
  /**
   * Opens the host's full "Remove Vendor" flow. Turning the recipient toggle off on a vendor that
   * isn't also a source is the same action, so it goes through the same confirmation. Without it
   * the recipient row renders as a plain statement.
   */
  onRemoveVendor?: () => void;
  removeVendorDisabled?: boolean;
  className?: string;
}

/**
 * A vendor's roles, recipient first.
 *
 * Recipient is a toggle that starts on (everything in the Vendor List is a recipient). Turning it
 * off removes the recipient role: for a vendor that is also a source that is a recipient-only
 * removal (it stays on the Sources page); otherwise it is the same as "Remove Vendor".
 *
 * Source only appears once the org has finished its Vendor List — onboarding users haven't met
 * sources yet, and the Sources step introduces them. A vendor that is already a source always
 * shows it, so it can be turned off.
 */
export function VendorDataSourceRecipientSection({
  orgId,
  vendor,
  setIsDisabledWhileLoading,
  addDataSource,
  removeDataSource,
  removeRecipientOnly,
  onRemoveVendor,
  removeVendorDisabled = false,
  className,
}: VendorDataSourceRecipientSectionProps) {
  const { vendorId, name: vendorName, isSource, isRecipient } = vendor;
  const showSnackbar = useSnackBar();
  const [onboardingProgress, onboardingProgressRequest] = useOnboardingProgress(orgId);
  const sourcesUnlocked =
    onboardingProgressRequest.success &&
    onboardingProgress.dataMapOnboardingProgress.isVendorInventoryComplete;
  const showSourceRow = isSource || sourcesUnlocked;
  const [confirmRecipientOnlyOpen, setConfirmRecipientOnlyOpen] = useState(false);

  // Resolves false (after an error toast) when the action fails, so callers skip their success toast.
  const withLoading = async (action: () => Promise<void>, errorMessage: string) => {
    setIsDisabledWhileLoading(true);
    try {
      await action();
      return true;
    } catch {
      showSnackbar(errorMessage, { variant: "error" });
      return false;
    } finally {
      setIsDisabledWhileLoading(false);
    }
  };

  const recipientToggleDisabled = !isSource && (removeVendorDisabled || !onRemoveVendor);
  const recipientToggle = (
    <Toggle
      checked={isRecipient}
      disabled={recipientToggleDisabled}
      label={`${vendorName} is a recipient of data`}
      size="small"
      onChange={(checked: boolean) => {
        if (checked) {
          return;
        }
        if (isSource) {
          setConfirmRecipientOnlyOpen(true);
        } else {
          onRemoveVendor?.();
        }
      }}
    />
  );

  return (
    <div className={clsx("p-[24px]", className)}>
      <div className="text-[16px] font-medium text-neutral-900">
        {showSourceRow ? "Data Recipient / Source" : "Data Recipient"}
      </div>
      <div className="mt-[6px] mb-[16px] text-[14px] leading-[20px] text-neutral-500">
        <span className="font-medium">Data Recipient</span> = a party to whom your business gives
        access to personal data, such as a service provider.
        {showSourceRow && (
          <>
            {" "}
            <span className="font-medium">Data Source</span> = a party from which your business
            collects personal data.
          </>
        )}
      </div>

      <div className="flex justify-between items-center">
        <div className="text-[14px] font-medium text-neutral-700">
          {vendorName} is {isRecipient ? "a" : "not a"}{" "}
          <span className="inline-block px-[10px] py-[2px] bg-green-100 text-green-800 rounded-[10px]">
            Recipient
          </span>{" "}
          of data
        </div>
        {isRecipient &&
          (recipientToggleDisabled ? (
            <ToolTip text={`${vendorName} can't be removed from your Vendor List.`} inline>
              <span className="inline-flex">{recipientToggle}</span>
            </ToolTip>
          ) : (
            recipientToggle
          ))}
      </div>

      {showSourceRow && (
        <div className="flex justify-between items-center mt-[12px]">
          <div className="text-[14px] font-medium text-neutral-700">
            {vendorName} is {isSource ? "a" : "not a"}{" "}
            <span className="inline-block px-[10px] py-[2px] bg-blue-100 text-blue-800 rounded-[10px]">
              Source
            </span>{" "}
            of data
          </div>
          <Toggle
            checked={isSource}
            label={`${vendorName} is a source of data`}
            size="small"
            onChange={async (checked: boolean) => {
              const verb = checked ? "add" : "remove";
              await withLoading(
                () => (checked ? addDataSource(vendorId) : removeDataSource(vendorId)),
                `Couldn't ${verb} ${vendorName} as a source. Please try again.`,
              );
            }}
          />
        </div>
      )}

      {confirmRecipientOnlyOpen && (
        <Modal
          title={`Remove ${vendorName} from your Vendor List?`}
          body={`${vendorName} will stay a data source on your Sources page. Its recipient settings and disclosures here will be removed.`}
          alignText="left"
          alignButton="right"
          firstButton="Cancel"
          onFirstButtonClick={() => setConfirmRecipientOnlyOpen(false)}
          secondButton="Remove as recipient"
          secondButtonColor="warning"
          onSecondButtonClick={async () => {
            setConfirmRecipientOnlyOpen(false);
            const removed = await withLoading(
              () => removeRecipientOnly(vendorId),
              `Couldn't remove ${vendorName} as a recipient. Please try again.`,
            );
            if (!removed) {
              return;
            }
            showSnackbar(`${vendorName} was removed from your Vendor List.`, {
              variant: "info",
              body: "It's still a data source on your Sources page.",
            });
          }}
          showOverlay
          onClose={() => setConfirmRecipientOnlyOpen(false)}
        />
      )}
    </div>
  );
}
